import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const TransporterComboBox = ({ 
  value, 
  onChange, 
  customerId, 
  dispatchMode,
  disabled = false,
  placeholder = "Enter transporter/courier name",
  autoSelectOnMount = false
}) => {
  const { token } = useAuth();
  const [history, setHistory] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch transporter history when customer changes
  useEffect(() => {
    if (!customerId || !token) {
      setHistory([]);
      return;
    }
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${API}/api/admin/customers/${customerId}/transporter-history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setHistory(data.history || []);
        }
      } catch (e) { /* silent */ }
    };
    fetchHistory();
  }, [customerId, token]);

  // Filter history based on input
  useEffect(() => {
    if (!value) {
      setFilteredHistory(history);
    } else {
      const q = value.toLowerCase();
      setFilteredHistory(history.filter(h => h.name.toLowerCase().includes(q)));
    }
  }, [value, history]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // RC-402 · When the parent requests auto-select-on-mount (typical
  // LR upload dialog), focus + select the pre-filled value once it
  // is in place. Delay matches Radix Dialog's open animation.
  useEffect(() => {
    if (!autoSelectOnMount || disabled) return;
    if (!value) return;
    const t = setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      try { el.focus(); el.select(); } catch (_) {}
    }, 150);
    return () => clearTimeout(t);
    // Only re-run when value transitions from empty → non-empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!value, autoSelectOnMount, disabled]);

  const label = dispatchMode === 'courier' ? 'Courier Name' : 'Transporter Name';

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange((e.target.value || '').toUpperCase())}
        onFocus={() => setShowDropdown(true)}
        disabled={disabled}
        placeholder={placeholder}
        className={`w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm uppercase ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`}
        data-testid="transporter-name-input"
        autoComplete="off"
      />
      {showDropdown && !disabled && filteredHistory.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
          data-testid="transporter-history-dropdown"
        >
          <div className="px-3 py-1.5 text-xs text-gray-400 font-medium border-b">
            Previously used
          </div>
          {filteredHistory.map((item, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => {
                onChange((item.name || '').toUpperCase());
                setShowDropdown(false);
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between transition-colors"
              data-testid={`transporter-option-${idx}`}
            >
              <span className="font-medium text-gray-800 uppercase">{item.name}</span>
              <span className="text-xs text-gray-400">
                {item.usage_count > 1 ? `${item.usage_count}x` : ''}
                {item.dispatch_mode === 'courier' ? ' (Courier)' : item.dispatch_mode === 'transport' ? ' (Transport)' : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransporterComboBox;
